import { z } from 'zod';

import { AgentConfigSchema } from '../agents/agent-config.schema';
import { AGENT_TOOL_ROOTS } from '../agents/agent-templates';
import { AGENT_TOOL_FIELDS } from '../agents/agent-tool-kinds';
import {
  AGENT_DONE_PORT,
  AGENT_LIMIT_PORT,
  ATTEMPT_TIMEOUT_SEC,
  CONTINUE_PORT,
  ENodeCategory,
  ENodeKind,
  ENodeType,
  ERROR_PORT,
  FLOW_LIMITS,
  MAIN_PORT,
  type TCopyEndStatus,
  type TSideEffects,
  WAIT_MAIN_PORT,
} from '../constants/flow.constants';
import type { TFlowNodeRetry } from '../schemas/flow-graph.schema';
import { canonicalKeyPath, type TTemplateRoot } from '../services/flow-template';
import {
  ActionConfigSchema,
  AggregateConfigSchema,
  AiClassifyConfigSchema,
  AiExtractConfigSchema,
  AiGenerateConfigSchema,
  CodeConfigSchema,
  DedupeConfigSchema,
  DeliverConfigSchema,
  FilterConfigSchema,
  LookupConfigSchema,
  LoopConfigSchema,
  ManualTriggerConfigSchema,
  MergeConfigSchema,
  RouteConfigSchema,
  ScheduleTriggerConfigSchema,
  SetConfigSchema,
  SplitConfigSchema,
  WaitConfigSchema,
  WebhookTriggerConfigSchema,
} from '../schemas/node-config.schemas';

/** What a copy becomes when it leaves through this output and no edge is drawn (§3.2). */
export interface IUnconnectedBehaviour {
  status: TCopyEndStatus;
  issue: boolean;
  label: string;
  /** Inside a loop body the copy does not end: the round ends, or the loop leaves by error. */
  loop?: { key: string; exit: 'round_end' | 'error' };
}

export interface INodeOutputSpec {
  key: string;
  label: string;
  main: boolean;
  unconnected: IUnconnectedBehaviour;
}

// `flow`: the flow an agent's Run a flow tool starts (agents §3.3).
export type TNodeRefKind = 'source' | 'destination' | 'transform_version' | 'credential' | 'flow';

export interface INodeRefSpec {
  kind: TNodeRefKind;
  id: string;
  field: string;
}

export interface INodeConditionSpec {
  field: string;
  form: unknown;
  /** The route output this condition selects; absent for filter and loop.while. */
  output?: string;
}

export interface INodeContextPathSpec {
  field: string;
  path: string;
}

type TConfig = Record<string, unknown>;

export type TNodeFieldKind =
  | 'text'
  | 'template'
  | 'number'
  | 'duration'
  | 'select'
  | 'condition'
  | 'path'
  | 'ref'
  | 'list'
  | 'record'
  | 'cron'
  // Connector fields (`@hookpipe/connectors` field.*): a switch, free JSON, a masked secret.
  | 'boolean'
  | 'json'
  | 'secret'
  // JavaScript in a code editor (the Code step's own `source`).
  | 'code';

export type TNodeOptionsSource =
  | 'sources'
  | 'destinations'
  | 'credentials'
  | 'transforms'
  // Library prompts (§16), picked with their version by the AI step's panel.
  | 'library'
  | 'connectors'
  | 'connector_actions'
  | 'models'
  | 'timezones'
  // A connector action's own dynamic list (Slack channels): asked of the connector itself.
  | 'connector_options';

/** A fixed select choice, human label included — the same shape a connector's dynamic options answer with. */
export interface INodeFieldOption {
  value: string;
  label: string;
}

/** One form field of the node panel; `path` is a config path, `*` standing for any item. */
export interface INodeFieldUi {
  path: string;
  label: string;
  kind: TNodeFieldKind;
  template: boolean;
  required: boolean;
  options?: readonly INodeFieldOption[];
  optionsSource?: TNodeOptionsSource;
  help?: string;
  placeholder?: string;
  multiline?: boolean;
  /** Shown only while the config value at `path` is `equals`. */
  visibleWhen?: { path: string; equals: string };
  /** Hidden while the config value at `path` is `equals` — the rest of the time it shows. */
  hiddenWhen?: { path: string; equals: string };
}

export interface INodeTypeDescriptor {
  type: ENodeType;
  kind: ENodeKind;
  category: ENodeCategory;
  label: string;
  configSchema: z.ZodType;
  fields: INodeFieldUi[];
  /** The only config paths where `{{ }}` is read (§5.3). */
  templateFields: string[];
  /** Roots those templates may start with; the default set when absent. */
  templateRoots?: readonly TTemplateRoot[];
  sideEffects: TSideEffects;
  /** Only async nodes that call out (not `wait`) keep attempts and retry. */
  retries: boolean;
  /** `null` with `retries` means "the destination's own policy" (deliver). */
  defaultRetry: TFlowNodeRetry | null;
  attemptTimeoutSec: number | null;
  /** "Skip in runs" passes a copy through the main output; a join or a split cannot. */
  disableable: boolean;
  /** Offers "If this step fails: go on" (`config.onError`, §3.2). */
  continueOnFail: boolean;
  // Every function below takes an unvalidated config: the canvas asks while the form is half-filled.
  inputs(config: TConfig): string[];
  outputs(config: TConfig): INodeOutputSpec[];
  refs(config: TConfig): INodeRefSpec[];
  conditions(config: TConfig): INodeConditionSpec[];
  contextPaths(config: TConfig): INodeContextPathSpec[];
}

const ENDS: IUnconnectedBehaviour = { status: 'completed', issue: false, label: 'ends here' };
const SYNC_ERROR: IUnconnectedBehaviour = { status: 'error', issue: true, label: 'error → issue' };
const ASYNC_ERROR: IUnconnectedBehaviour = {
  status: 'failed',
  issue: true,
  label: 'error → issue',
};
const QUIET: IUnconnectedBehaviour = {
  status: 'filtered',
  issue: false,
  label: 'ends here, quietly',
};
const DUPLICATE: IUnconnectedBehaviour = { status: 'duplicate', issue: false, label: 'ends here' };
const STOPS: IUnconnectedBehaviour = { status: 'stopped', issue: false, label: 'stops here' };

const out = (key: string, unconnected: IUnconnectedBehaviour, main = false): INodeOutputSpec => ({
  key,
  label: key,
  main,
  unconnected,
});

const mainOut = (key: string): INodeOutputSpec => out(key, ENDS, true);

function str(config: TConfig, key: string): string | null {
  const value = config[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function list(config: TConfig, key: string): Record<string, unknown>[] {
  const value = config[key];
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => typeof item === 'object' && !!item)
    : [];
}

function ref(config: TConfig, key: string, kind: TNodeRefKind): INodeRefSpec[] {
  const value = str(config, key);
  return value ? [{ kind, id: value, field: key }] : [];
}

function formPaths(form: unknown, field: string): INodeContextPathSpec[] {
  const groups = (form as { groups?: unknown })?.groups;
  if (!Array.isArray(groups)) return [];
  return groups.flatMap((group, g) => {
    const conditions = (group as { conditions?: unknown })?.conditions;
    if (!Array.isArray(conditions)) return [];
    return conditions
      .map((condition, c) => ({ condition: condition as { path?: unknown }, c }))
      .filter(({ condition }) => typeof condition?.path === 'string')
      .map(({ condition, c }) => ({
        field: `${field}.groups.${g}.conditions.${c}.path`,
        path: condition.path as string,
      }));
  });
}

type TFieldOptions = Partial<
  Pick<
    INodeFieldUi,
    | 'template'
    | 'options'
    | 'optionsSource'
    | 'help'
    | 'placeholder'
    | 'multiline'
    | 'visibleWhen'
    | 'hiddenWhen'
  >
> & {
  optional?: boolean;
};

const field = (
  path: string,
  label: string,
  kind: TNodeFieldKind,
  options: TFieldOptions = {},
): INodeFieldUi => ({
  path,
  label,
  kind,
  template: options.template ?? kind === 'template',
  required: !options.optional,
  ...(options.options ? { options: options.options } : {}),
  ...(options.optionsSource ? { optionsSource: options.optionsSource } : {}),
  ...(options.help ? { help: options.help } : {}),
  ...(options.placeholder ? { placeholder: options.placeholder } : {}),
  ...(options.multiline ? { multiline: true } : {}),
  ...(options.visibleWhen ? { visibleWhen: options.visibleWhen } : {}),
  ...(options.hiddenWhen ? { hiddenWhen: options.hiddenWhen } : {}),
});

const optional: TFieldOptions = { optional: true };

const none = () => [];
const single = () => [MAIN_PORT];

interface IDescriptorDraft extends Partial<Omit<INodeTypeDescriptor, 'templateFields'>> {
  type: ENodeType;
  kind: ENodeKind;
  category: ENodeCategory;
  label: string;
  configSchema: z.ZodType;
  fields: INodeFieldUi[];
  outputs(config: TConfig): INodeOutputSpec[];
}

const NOT_DISABLEABLE: readonly ENodeKind[] = [
  ENodeKind.TRIGGER,
  ENodeKind.JOIN,
  ENodeKind.CONTAINER,
];

const ON_ERROR_FIELDS: INodeFieldUi[] = [
  field('onError', 'If this step fails', 'select', {
    options: [
      { value: 'stop', label: 'Stop and open an issue' },
      { value: 'continue', label: 'Go on to the next step' },
    ],
    optional: true,
  }),
  field('raiseIssue', 'Open an issue anyway', 'boolean', {
    optional: true,
    visibleWhen: { path: 'onError', equals: 'continue' },
  }),
];

const CLASSIFY_ON_ERROR_HELP = 'Going on, a failed answer leaves by the category used when unsure.';

function define(draft: IDescriptorDraft): INodeTypeDescriptor {
  const continueOnFail = draft.continueOnFail ?? false;
  const own = draft.outputs;
  const onErrorFields =
    draft.type === ENodeType.AI_CLASSIFY
      ? [{ ...ON_ERROR_FIELDS[0], help: CLASSIFY_ON_ERROR_HELP }, ON_ERROR_FIELDS[1]]
      : ON_ERROR_FIELDS;
  const fields = continueOnFail ? [...draft.fields, ...onErrorFields] : draft.fields;
  return {
    sideEffects: 'none',
    retries: false,
    defaultRetry: null,
    attemptTimeoutSec: null,
    disableable: !NOT_DISABLEABLE.includes(draft.kind) && draft.type !== ENodeType.SPLIT,
    inputs: draft.kind === ENodeKind.TRIGGER ? none : single,
    refs: none,
    conditions: none,
    contextPaths: none,
    ...draft,
    continueOnFail,
    fields,
    outputs: continueOnFail ? (config) => continuedOutputs(draft.type, config, own(config)) : own,
    templateFields: fields.filter((f) => f.template).map((f) => f.path),
  };
}

// `error` goes; a single `ok` becomes `done`. Classify keeps its categories (§3.2).
function continuedOutputs(
  type: ENodeType,
  config: TConfig,
  specs: INodeOutputSpec[],
): INodeOutputSpec[] {
  if (!continueOutputOf(type, config)) return specs;
  const kept = specs.filter((spec) => spec.key !== ERROR_PORT);
  return type === ENodeType.AI_CLASSIFY ? kept : [mainOut(CONTINUE_PORT)];
}

/** The output a failure leaves by when the step goes on; `null` when it stops (the default). */
function continueOutputOf(type: string, config: TConfig): string | null {
  if (config.onError !== 'continue') return null;
  if (type === ENodeType.AI_CLASSIFY) return str(config, 'fallbackCategory');
  return CONTINUE_PORT;
}

export interface IContinueOnFail {
  output: string;
  raiseIssue: boolean;
}

/** `null`: the step stops on failure — `error` output, an issue when it is not connected. */
export function continueOnFailOf(node: {
  type: string;
  config?: TConfig | null;
}): IContinueOnFail | null {
  if (!nodeTypeOf(node.type)?.continueOnFail) return null;
  const config = node.config ?? {};
  const output = continueOutputOf(node.type, config);
  return output ? { output, raiseIssue: config.raiseIssue === true } : null;
}

/**
 * An edge drawn from `ok` reads as `done` once its step goes on after failing, and back again
 * when it stops (§3.4): switching the setting never strands the success path.
 */
export function onErrorEdgeOutput(node: { type: string; config?: TConfig | null }, output: string) {
  if (node.type === ENodeType.AI_CLASSIFY || !nodeTypeOf(node.type)?.continueOnFail) return output;
  const going = continueOnFailOf(node) !== null;
  if (going && output === 'ok') return CONTINUE_PORT;
  if (!going && output === CONTINUE_PORT) return 'ok';
  return output;
}

const retry = (maxAttempts: number, initialSec: number, capSec: number): TFlowNodeRetry => ({
  strategy: 'exponential',
  maxAttempts,
  initialSec,
  multiplier: 2,
  capSec,
  jitterPct: 10,
  onAmbiguous: 'retry',
});

const okError = (kind: ENodeKind) => () => [
  mainOut('ok'),
  out(ERROR_PORT, kind === ENodeKind.ASYNC ? ASYNC_ERROR : SYNC_ERROR),
];

const aiFields = [
  field('credentialId', 'Credentials', 'ref', { optionsSource: 'credentials' }),
  field('model', 'Model', 'select', {
    optionsSource: 'models',
    help: 'The model that answers. Larger ones follow long instructions better; smaller ones are faster and cost less per token.',
  }),
  // The prompt: the step's own messages, or a pinned Library prompt and its variables (§16).
  field('messages', 'Prompt', 'list', {
    optional: true,
    help: 'System sets the rules: who the model is and how to answer. User carries the copy’s data — type {{ to insert it. Assistant writes an example answer the model continues from.',
  }),
  field('messages.*.role', 'Role', 'select', {
    options: [
      { value: 'system', label: 'System' },
      { value: 'user', label: 'User' },
      { value: 'assistant', label: 'Assistant' },
    ],
  }),
  field('messages.*.content', 'Message', 'template', { multiline: true }),
  field('library', 'Prompt from the Library', 'ref', { optionsSource: 'library', optional: true }),
  field('library.vars.*', 'Variable', 'template', optional),
  field('maxTokens', 'Longest answer, tokens', 'number', {
    optional: true,
    help: 'Where the answer is cut off. A token is about ¾ of a word, and the model’s thinking counts too.',
  }),
  field('temperature', 'Temperature', 'number', {
    optional: true,
    help: 'From 0 to 2. 0 answers the same way every time; higher varies the wording. Empty uses the model’s own. Keep it low to classify or extract.',
  }),
];

const aiNode = (
  type: ENodeType,
  label: string,
  schema: z.ZodType,
  extra: INodeFieldUi[] = [],
  outputs: (config: TConfig) => INodeOutputSpec[] = okError(ENodeKind.ASYNC),
) =>
  define({
    type,
    kind: ENodeKind.ASYNC,
    category: ENodeCategory.AI,
    label,
    configSchema: schema,
    fields: [...aiFields, ...extra],
    // Models are always called in a test run: the branch you see is the one you would get (§8.3).
    sideEffects: 'none',
    retries: true,
    defaultRetry: retry(3, 10, 20),
    attemptTimeoutSec: ATTEMPT_TIMEOUT_SEC.ai,
    continueOnFail: true,
    outputs,
    refs: (config) => [...ref(config, 'credentialId', 'credential'), ...libraryRef(config)],
  });

// A pinned Library prompt is a `transform_version` ref: the same table, FK and "used in" (§16).
function libraryRef(config: TConfig): INodeRefSpec[] {
  const library = config.library;
  if (!library || typeof library !== 'object') return [];
  const versionId = str(library as TConfig, 'versionId');
  return versionId
    ? [{ kind: 'transform_version', id: versionId, field: 'library.versionId' }]
    : [];
}

const DESCRIPTORS: INodeTypeDescriptor[] = [
  define({
    type: ENodeType.TRIGGER_WEBHOOK,
    kind: ENodeKind.TRIGGER,
    category: ENodeCategory.TRIGGER,
    label: 'Webhook',
    configSchema: WebhookTriggerConfigSchema,
    fields: [field('sourceId', 'Source', 'ref', { optionsSource: 'sources' })],
    outputs: () => [mainOut(MAIN_PORT)],
    refs: (config) => ref(config, 'sourceId', 'source'),
  }),
  define({
    type: ENodeType.TRIGGER_SCHEDULE,
    kind: ENodeKind.TRIGGER,
    category: ENodeCategory.TRIGGER,
    label: 'Schedule',
    configSchema: ScheduleTriggerConfigSchema,
    fields: [
      field('cron', 'Runs at', 'cron'),
      field('timezone', 'Time zone', 'select', { optionsSource: 'timezones', optional: true }),
      field('overlap', 'If the last run is still going', 'select', {
        options: [
          { value: 'skip', label: 'Skip this run' },
          { value: 'allow', label: 'Start it anyway' },
        ],
        optional: true,
      }),
      field('catchUp', 'After downtime', 'select', {
        options: [
          { value: 'one', label: 'Run once for the time missed' },
          { value: 'none', label: 'Skip what was missed' },
        ],
        optional: true,
      }),
      field('input', 'Input', 'record', optional),
    ],
    outputs: () => [mainOut(MAIN_PORT)],
  }),
  define({
    type: ENodeType.TRIGGER_MANUAL,
    kind: ENodeKind.TRIGGER,
    category: ENodeCategory.TRIGGER,
    label: 'Manual',
    configSchema: ManualTriggerConfigSchema,
    fields: [
      field('inputFields', 'Input fields', 'list', {
        optional: true,
        help: 'The Run now form asks for these and a run must match them. None: free JSON.',
      }),
      field('inputFields.*.name', 'Name', 'text', { placeholder: 'customer_id' }),
      field('inputFields.*.kind', 'Kind', 'select', {
        options: [
          { value: 'text', label: 'Text' },
          { value: 'number', label: 'Number' },
          { value: 'boolean', label: 'Yes / no' },
          { value: 'json', label: 'JSON' },
        ],
        optional: true,
      }),
      field('inputFields.*.required', 'Required', 'boolean', optional),
      field('inputFields.*.description', 'Description', 'text', optional),
      field('sampleInput', 'Sample input', 'record', optional),
    ],
    outputs: () => [mainOut(MAIN_PORT)],
  }),
  define({
    type: ENodeType.FILTER,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.LOGIC,
    label: 'Filter',
    configSchema: FilterConfigSchema,
    fields: [field('condition', 'Keep copies where', 'condition')],
    outputs: () => [mainOut('kept'), out('dropped', QUIET)],
    conditions: (config) => [{ field: 'condition', form: config.condition }],
    contextPaths: (config) => formPaths(config.condition, 'condition'),
  }),
  define({
    type: ENodeType.DEDUPE,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.LOGIC,
    label: 'Dedupe',
    configSchema: DedupeConfigSchema,
    fields: [
      field('keyPaths', 'Same when these match', 'list'),
      field('windowSec', 'Within', 'duration', optional),
    ],
    outputs: () => [mainOut('new'), out('duplicate', DUPLICATE)],
    contextPaths: (config) =>
      (Array.isArray(config.keyPaths) ? config.keyPaths : [])
        .map((path, i) => ({ field: `keyPaths.${i}`, path }))
        .filter((spec): spec is { field: string; path: string } => typeof spec.path === 'string')
        .map((spec) => ({ ...spec, path: canonicalKeyPath(spec.path) })),
  }),
  define({
    type: ENodeType.ROUTE,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.LOGIC,
    label: 'Route',
    configSchema: RouteConfigSchema,
    fields: [
      field('routes', 'Routes', 'list'),
      field('routes.*.key', 'Output', 'text'),
      field('routes.*.name', 'Name', 'text'),
      field('routes.*.condition', 'When', 'condition'),
    ],
    outputs: (config) => {
      const routes = list(config, 'routes')
        .map((route) => route.key)
        .filter((key): key is string => typeof key === 'string' && key !== '');
      return [...routes.map((key, i) => out(key, ENDS, i === 0)), out('otherwise', ENDS)];
    },
    conditions: (config) =>
      list(config, 'routes').map((route, i) => ({
        field: `routes.${i}.condition`,
        form: route.condition,
        output: typeof route.key === 'string' ? route.key : undefined,
      })),
    contextPaths: (config) =>
      list(config, 'routes').flatMap((route, i) =>
        formPaths(route.condition, `routes.${i}.condition`),
      ),
  }),
  define({
    type: ENodeType.SPLIT,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.LOGIC,
    label: 'Split',
    configSchema: SplitConfigSchema,
    fields: [
      field('path', 'List to split', 'path'),
      field('maxItems', 'At most', 'number', optional),
    ],
    outputs: () => [mainOut('item'), out('empty', STOPS), out(ERROR_PORT, SYNC_ERROR)],
    contextPaths: (config) => {
      const path = str(config, 'path');
      return path ? [{ field: 'path', path }] : [];
    },
  }),
  define({
    type: ENodeType.AGGREGATE,
    kind: ENodeKind.JOIN,
    category: ENodeCategory.LOGIC,
    label: 'Aggregate',
    configSchema: AggregateConfigSchema,
    fields: [field('timeoutSec', 'Stop waiting after', 'duration', optional)],
    outputs: () => [mainOut(MAIN_PORT), out(ERROR_PORT, SYNC_ERROR)],
  }),
  define({
    type: ENodeType.MERGE,
    kind: ENodeKind.JOIN,
    category: ENodeCategory.LOGIC,
    label: 'Merge',
    configSchema: MergeConfigSchema,
    fields: [
      field('inputs', 'Inputs', 'number', optional),
      field('mode', 'Go on when', 'select', {
        options: [
          { value: 'first', label: 'First' },
          { value: 'all', label: 'Every' },
          { value: 'combine', label: 'Combine' },
          { value: 'collect', label: 'Collect' },
        ],
        optional: true,
      }),
      field('combines', 'Combine', 'select', {
        options: [
          { value: 'results', label: 'The answer of the step before each input' },
          { value: 'events', label: 'The fields of each input’s event' },
        ],
        optional: true,
        visibleWhen: { path: 'mode', equals: 'combine' },
        help: 'answers come out as one object with a field per input: 1, 2, …',
      }),
      // "First" goes on with whatever arrives first and never waits, so it has nothing to time out.
      field('timeoutSec', 'Stop waiting after', 'duration', {
        optional: true,
        hiddenWhen: { path: 'mode', equals: 'first' },
        help: 'then it leaves by the error output — every, combine and collect have one; first does not',
      }),
    ],
    inputs: (config) => {
      const raw = typeof config.inputs === 'number' ? Math.trunc(config.inputs) : 2;
      const count = Math.min(Math.max(raw, 1), FLOW_LIMITS.maxMergeInputs);
      return Array.from({ length: count }, (_, i) => String(i + 1));
    },
    outputs: (config) =>
      config.mode === 'first'
        ? [mainOut(MAIN_PORT)]
        : [mainOut(MAIN_PORT), out(ERROR_PORT, SYNC_ERROR)],
  }),
  define({
    type: ENodeType.LOOP,
    kind: ENodeKind.CONTAINER,
    category: ENodeCategory.LOGIC,
    label: 'Loop',
    configSchema: LoopConfigSchema,
    fields: [
      field('while', 'Repeat while', 'condition'),
      field('maxIterations', 'At most, rounds', 'number', optional),
    ],
    outputs: () => [mainOut('done'), out(ERROR_PORT, SYNC_ERROR)],
    conditions: (config) => [{ field: 'while', form: config.while }],
    contextPaths: (config) => formPaths(config.while, 'while'),
  }),
  define({
    type: ENodeType.WAIT,
    kind: ENodeKind.ASYNC,
    category: ENodeCategory.LOGIC,
    label: 'Wait',
    configSchema: WaitConfigSchema,
    fields: [
      field('mode', 'How long', 'select', {
        options: [
          { value: 'duration', label: 'Wait for a set time' },
          { value: 'until', label: 'Wait until a time in the event' },
        ],
      }),
      field('durationSec', 'For', 'duration', {
        optional: true,
        visibleWhen: { path: 'mode', equals: 'duration' },
      }),
      field('untilPath', 'Until the time in', 'path', {
        optional: true,
        visibleWhen: { path: 'mode', equals: 'until' },
      }),
      field('maxWaitSec', 'But no longer than', 'duration', {
        optional: true,
        visibleWhen: { path: 'mode', equals: 'until' },
      }),
    ],
    outputs: () => [mainOut(WAIT_MAIN_PORT), out('missing', STOPS)],
    contextPaths: (config) => {
      const path = str(config, 'untilPath');
      return config.mode === 'until' && path ? [{ field: 'untilPath', path }] : [];
    },
  }),
  define({
    type: ENodeType.CODE,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.TRANSFORM,
    label: 'Code',
    configSchema: CodeConfigSchema,
    // `source` is the step's own code; the shared-code pair only on flows that already pin one.
    fields: [
      field('source', 'Code', 'code', {
        optional: true,
        help: '`export default (event, ctx) => newEvent` — return nothing to drop the copy.',
      }),
      field('transformId', 'Shared code', 'ref', { optionsSource: 'transforms', optional: true }),
      field('version', 'Version', 'number', optional),
    ],
    continueOnFail: true,
    outputs: okError(ENodeKind.SYNC),
    refs: (config) => ref(config, 'transformVersionId', 'transform_version'),
  }),
  define({
    type: ENodeType.SET,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.TRANSFORM,
    label: 'Set fields',
    configSchema: SetConfigSchema,
    fields: [
      field('fields', 'Fields', 'list'),
      field('fields.*.path', 'Field', 'path'),
      field('fields.*.value', 'Value', 'template'),
    ],
    continueOnFail: true,
    outputs: okError(ENodeKind.SYNC),
  }),
  define({
    type: ENodeType.LOOKUP,
    kind: ENodeKind.SYNC,
    category: ENodeCategory.TRANSFORM,
    label: 'Lookup table',
    configSchema: LookupConfigSchema,
    fields: [
      field('value', 'Look up', 'template'),
      field('target', 'Write to', 'path'),
      field('table', 'Table', 'list'),
      field('ifNotFound', 'If not found', 'select', {
        options: [
          { value: 'keep', label: 'Keep as is' },
          { value: 'default', label: 'Use a default' },
          { value: 'error', label: 'Error' },
        ],
      }),
      field('default', 'Otherwise', 'text', optional),
    ],
    continueOnFail: true,
    outputs: okError(ENodeKind.SYNC),
  }),
  define({
    type: ENodeType.DELIVER,
    kind: ENodeKind.ASYNC,
    category: ENodeCategory.SEND,
    label: 'Deliver',
    configSchema: DeliverConfigSchema,
    fields: [
      field('destinationId', 'Destination', 'ref', { optionsSource: 'destinations' }),
      field('sends', 'Sends', 'select', {
        options: [
          { value: 'whole', label: 'The copy as is' },
          { value: 'field', label: 'One field' },
        ],
      }),
      field('fieldPath', 'Field', 'path', {
        optional: true,
        visibleWhen: { path: 'sends', equals: 'field' },
      }),
    ],
    sideEffects: 'external',
    retries: true,
    attemptTimeoutSec: ATTEMPT_TIMEOUT_SEC.deliver,
    continueOnFail: true,
    outputs: okError(ENodeKind.ASYNC),
    refs: (config) => ref(config, 'destinationId', 'destination'),
    contextPaths: (config) => {
      const path = str(config, 'fieldPath');
      return config.sends === 'field' && path ? [{ field: 'fieldPath', path }] : [];
    },
  }),
  define({
    type: ENodeType.ACTION,
    kind: ENodeKind.ASYNC,
    category: ENodeCategory.SEND,
    label: 'App action',
    configSchema: ActionConfigSchema,
    fields: [
      field('connector', 'App', 'select', { optionsSource: 'connectors' }),
      field('action', 'Action', 'select', { optionsSource: 'connector_actions' }),
      field('credentialId', 'Credentials', 'ref', { optionsSource: 'credentials', optional: true }),
      field('input', 'Input', 'record', { template: true, optional: true }),
    ],
    // The conservative answer for an action not chosen yet; once it is, the connector registry
    // answers per action (flow/connectors `ConnectorCatalog.sideEffectsOf`, §6.1).
    sideEffects: 'external',
    retries: true,
    defaultRetry: retry(3, 20, 40),
    attemptTimeoutSec: ATTEMPT_TIMEOUT_SEC.action,
    continueOnFail: true,
    outputs: okError(ENodeKind.ASYNC),
    refs: (config) => ref(config, 'credentialId', 'credential'),
  }),
  aiNode(ENodeType.AI_GENERATE, 'Generate text', AiGenerateConfigSchema),
  aiNode(ENodeType.AI_EXTRACT, 'Extract fields', AiExtractConfigSchema, [
    field('fields', 'Fields to extract', 'list', {
      help: 'Each field is one value the model pulls out and returns under that name. The description says what to look for.',
    }),
  ]),
  aiNode(
    ENodeType.AI_CLASSIFY,
    'Classify',
    AiClassifyConfigSchema,
    [
      field('categories', 'Categories', 'list', {
        help: 'The model reads the last User message and picks one category by its name and description. Each category is one output of this step.',
      }),
      field('fallbackCategory', 'When unsure', 'select', {
        optional: true,
        help: 'Where a copy goes when no category fits. With no fallback it fails and leaves by error.',
      }),
    ],
    (config) => {
      const keys = list(config, 'categories')
        .map((category) => category.key)
        .filter((key): key is string => typeof key === 'string' && key !== '');
      return [...keys.map((key, i) => out(key, ENDS, i === 0)), out(ERROR_PORT, ASYNC_ERROR)];
    },
  ),
  define({
    type: ENodeType.AI_AGENT,
    kind: ENodeKind.ASYNC,
    category: ENodeCategory.AI,
    label: 'AI agent',
    configSchema: AgentConfigSchema,
    fields: [
      ...aiFields.filter((ui) => ui.path !== 'temperature'),
      field('thinking', 'Thinking', 'select', {
        options: [
          { value: 'adaptive', label: 'Think when it helps' },
          { value: 'off', label: 'Off' },
        ],
        optional: true,
      }),
      field('tools', 'Tools', 'list', {
        optional: true,
        help: 'What the agent may do. It sees each tool’s name, description and the fields it fills; fixed fields it never sees.',
      }),
      field('tools.*.fields.*.value', 'Fixed value', 'template', optional),
      ...toolFieldLabels(),
      field('tools.*.config.input', 'Input', 'record', { template: true, optional: true }),
      field('limits.maxSteps', 'Steps', 'number', optional),
      field('limits.maxCostUsd', 'Cost a run', 'number', {
        optional: true,
        help: 'Checked before every model call by an estimate; empty is no limit.',
      }),
      field('limits.maxActiveSec', 'Time', 'duration', {
        optional: true,
        help: 'Only its own work counts: model calls and tools, not waiting.',
      }),
      field('onBudgetExhausted', 'When the AI budget runs out', 'select', {
        options: [
          { value: 'hold', label: 'Wait for the budget' },
          { value: 'limit', label: 'Leave by limit instead' },
        ],
        optional: true,
      }),
      field('output.kind', 'Answer', 'select', {
        options: [
          { value: 'text', label: 'Text' },
          { value: 'json', label: 'JSON fields' },
          { value: 'categories', label: 'One of categories' },
        ],
        optional: true,
      }),
      field('output.fields', 'Fields', 'list', {
        optional: true,
        visibleWhen: { path: 'output.kind', equals: 'json' },
      }),
      field('output.categories', 'Categories', 'list', {
        optional: true,
        visibleWhen: { path: 'output.kind', equals: 'categories' },
      }),
      field('toolResultMaxChars', 'A tool result it reads, characters', 'number', optional),
    ],
    templateRoots: AGENT_TOOL_ROOTS,
    // Its tools may change data; a draft test runs it only in its own agent test (§11).
    sideEffects: 'external',
    retries: true,
    defaultRetry: retry(3, 5, 30),
    attemptTimeoutSec: ATTEMPT_TIMEOUT_SEC.ai,
    outputs: () => [
      mainOut(AGENT_DONE_PORT),
      out(AGENT_LIMIT_PORT, { status: 'stopped', issue: false, label: 'stops here' }),
      out(ERROR_PORT, ASYNC_ERROR),
    ],
    refs: (config) => [
      ...ref(config, 'credentialId', 'credential'),
      ...libraryRef(config),
      ...list(config, 'tools').flatMap((tool, i) => toolRefs(tool, i)),
    ],
  }),
];

// Labels only (a `*` path is never a form field of its own): what a finding inside a tool says.
function toolFieldLabels(): INodeFieldUi[] {
  const own = new Set(['fields.*.value', 'config.input']);
  const seen = new Set<string>();
  return AGENT_TOOL_FIELDS.filter((each) => {
    if (own.has(each.path) || seen.has(each.path)) return false;
    seen.add(each.path);
    return true;
  }).map((each) =>
    field(`tools.*.${each.path}`, each.label, 'text', {
      optional: true,
      ...(each.help ? { help: each.help } : {}),
    }),
  );
}

function toolRefs(tool: TConfig, index: number): INodeRefSpec[] {
  const config = (tool.config ?? {}) as TConfig;
  const at = `tools.${index}.config`;
  const own = (key: string, kind: TNodeRefKind): INodeRefSpec[] =>
    ref(config, key, kind).map((spec) => ({ ...spec, field: `${at}.${spec.field}` }));
  if (tool.kind === 'action') return own('credentialId', 'credential');
  if (tool.kind === 'code') return own('versionId', 'transform_version');
  if (tool.kind === 'run_flow') return own('flowId', 'flow');
  return [];
}

const NODE_TYPE_REGISTRY: ReadonlyMap<string, INodeTypeDescriptor> = new Map(
  DESCRIPTORS.map((descriptor) => [descriptor.type, descriptor]),
);

export function nodeTypeOf(type: string): INodeTypeDescriptor | undefined {
  return NODE_TYPE_REGISTRY.get(type);
}

export interface INodeTypeCatalogEntry {
  type: ENodeType;
  kind: ENodeKind;
  category: ENodeCategory;
  label: string;
  sideEffects: TSideEffects;
  retries: boolean;
  defaultRetry: TFlowNodeRetry | null;
  disableable: boolean;
  continueOnFail: boolean;
  configJsonSchema: unknown;
  fields: INodeFieldUi[];
  defaultOutputs: INodeOutputSpec[];
  defaultInputs: string[];
}

/** The palette and panel forms, serialisable for `GET /v1/flows/node-types`. */
export function nodeTypeCatalog(): INodeTypeCatalogEntry[] {
  return DESCRIPTORS.map((d) => ({
    type: d.type,
    kind: d.kind,
    category: d.category,
    label: d.label,
    sideEffects: d.sideEffects,
    retries: d.retries,
    defaultRetry: d.defaultRetry,
    disableable: d.disableable,
    continueOnFail: d.continueOnFail,
    configJsonSchema: z.toJSONSchema(d.configSchema, { io: 'input', unrepresentable: 'any' }),
    fields: d.fields,
    defaultOutputs: d.outputs({}),
    defaultInputs: d.inputs({}),
  }));
}
